import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { Box } from '@mui/material'
import Button from '../Button'
import Modal from '../modal/Modal'
import ModalTitle from '../modal/ModalTitle'
import ModalContent from '../modal/ModalContent'
import { REQUIRED_MESSAGE } from '../../features/login/constants'
import FormTextField from '../formControls/FormTextField'
import { ReactComponent as EmailIcon } from '../../features/login/assets/Email.svg'
import { TForm } from '../../features/login/definitions'
import { useExportResultMutation } from './queries/useExportResultQuery'
import { useNotificationContext } from '../../features/notifications/state/useNotification'
import ErrorNotification from '../../features/notifications/components/ErrorNotification'
import { EMAIL_REGEXP } from '../../features/userManagement/regexp'
import { INVALID_EMAIL_MESSAGE } from '../../features/userManagement/constants'

const FormStyled = styled.form`
  display: grid;
  grid-row-gap: 16px;
  width: 100%
`

const ExportButton = () => {
  const { mutateAsync: exportResult, isLoading } = useExportResultMutation()
  const { showErrorNotification, showSuccessNotification } = useNotificationContext()

  const [isOpen, setIsOpen] = useState(false)
  const [htmlContent, setHtmlContent] = useState('')
  const [isPreparingExport, setIsPreparingExport] = useState(false)

  const {
    control,
    formState,
    clearErrors,
    reset,
    handleSubmit
  } = useForm<TForm>({
    defaultValues: {
      email: ''
    },
    mode: 'onSubmit'
  })
  const { errors, isDirty } = formState

  const onSubmit = async (data: any) => {
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const file = new File([blob], 'page-export.html', { type: 'text/html' })

    const payload = {
      email: data.email,
      file
    }
    try {
      await exportResult(payload)
      showSuccessNotification(<Box>Your search results have been sent to the email address you provided.</Box>)
    } catch (error: any) {
      showErrorNotification(<ErrorNotification message={error?.response?.data ? error?.response?.data : 'Something went wrong'} />)
    } finally {
      reset()
      setIsOpen(false)
    }
  }

  const prepareHtmlWithInlinedStyles = useCallback(async () => {
    const originalHtml = document.documentElement.cloneNode(true) as HTMLElement
    const head = originalHtml.querySelector('head')

    if (!head) {
      return document.documentElement.outerHTML
    }

    const { doctype } = document
    const doctypeString = doctype
      ? `<!DOCTYPE ${doctype.name}${doctype.publicId ? ` PUBLIC "${doctype.publicId}"` : ''}${doctype.systemId ? ` "${doctype.systemId}"` : ''}>`
      : '<!DOCTYPE html>'

    const collectCssChunks = async (): Promise<string> => {
      const styleSheets = Array.from(document.styleSheets)

      const cssTexts = await Promise.all(styleSheets.map(async (sheet) => {
        if (!(sheet instanceof CSSStyleSheet)) {
          return ''
        }

        try {
          return Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join('\n')
        } catch (error) {
          const { href } = sheet

          if (!href) {
            console.error('Failed to read CSS rules from inline stylesheet for export', error)
            return ''
          }

          try {
            const absoluteHref = new URL(href, window.location.href).href
            const response = await fetch(absoluteHref, { credentials: 'include' })

            if (!response.ok) {
              throw new Error(`Failed to fetch stylesheet: ${absoluteHref}`)
            }

            return await response.text()
          } catch (fetchError) {
            console.error('Failed to inline external stylesheet for export', fetchError)
            return ''
          }
        }
      }))

      return cssTexts.filter(Boolean).join('\n')
    }

    const combinedCss = await collectCssChunks()

    if (combinedCss) {
      const inlineStyle = document.createElement('style')

      inlineStyle.appendChild(document.createTextNode(combinedCss))
      head.appendChild(inlineStyle)
    }

    const allStyleTagsForCleanup = Array.from(originalHtml.querySelectorAll('style')) as HTMLStyleElement[]

    allStyleTagsForCleanup.forEach((styleTag) => {
      Array.from(styleTag.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-')) {
          styleTag.removeAttribute(attr.name)
        }
      })
    })

    return `${doctypeString}${originalHtml.outerHTML}`
  }, [])

  useEffect(() => {
    if (isDirty && (errors.email || errors.password)) {
      clearErrors()
    }
  }, [clearErrors, errors, isDirty])

  const handleOpenModal = useCallback(async () => {
    try {
      setIsPreparingExport(true)
      const html = await prepareHtmlWithInlinedStyles()

      setHtmlContent(html)
      setIsOpen(true)
    } catch (error) {
      console.error('Failed to prepare HTML for export', error)
      showErrorNotification(<ErrorNotification message="Something went wrong while preparing export. Please try again." />)
    } finally {
      setIsPreparingExport(false)
    }
  }, [prepareHtmlWithInlinedStyles, showErrorNotification])

  return (
    <>
      <Button
        onClick={handleOpenModal}
        loading={isPreparingExport}
      >Export
      </Button>
      <Modal open={isOpen} closeModal={() => setIsOpen(false)} withCloseButton>
        <ModalTitle>
          The content of this page will be emailed to
        </ModalTitle>
        <ModalContent>
          <FormStyled onSubmit={handleSubmit(onSubmit)}>
            <FormTextField
              id="email"
              label="Enter Email"
              fullWidth
              name="email"
              placeholder="email@domain.com"
              startIcon={<EmailIcon />}
              type="email"
              control={control}
              rules={{
                required: { value: true, message: REQUIRED_MESSAGE },
                pattern: { value: EMAIL_REGEXP, message: INVALID_EMAIL_MESSAGE }
              }}
            />
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                loading={isLoading}
                variant="primary"
                type="submit"
              >
                Ok
              </Button>
            </Box>
          </FormStyled>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ExportButton
